import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography
} from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../Api/endpoints';
import { ROUTES } from '../../../routes';

function EmployeeLoginActivation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { employeeId } = useParams();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    accessLevel: 'CUSTOMER_EMPLOYEE'
  });

  const [errors, setErrors] = useState({});

  // Get auth token from localStorage
  const getAuthToken = () => localStorage.getItem('authToken');

  // Load employee data if passed from navigation state
  useEffect(() => {
    if (location.state?.employee) {
      setEmployee(location.state.employee);
    } else {
      // If no employee data in state, fetch it using the employeeId
      fetchEmployeeData();
    }
  }, [location.state, employeeId]);

  // Fetch employee data if not provided in navigation state
  const fetchEmployeeData = async () => {
    if (!employeeId) {
      setError('Employee ID is missing');
      return;
    }

    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await axios.get(API_ENDPOINTS.EMPLOYEES.DETAIL(employeeId), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data && response.data.data) {
        setEmployee(response.data.data);
      } else {
        setError('Failed to load employee data');
      }
    } catch (error) {
      console.error('Error fetching employee data:', error);
      setError('Failed to load employee data');
    } finally {
      setLoading(false);
    }
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 4) {
      newErrors.username = 'Username must be at least 4 characters';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Access level validation
    if (!formData.accessLevel) {
      newErrors.accessLevel = 'Access level is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const token = getAuthToken();

      // Prepare the request payload
      const payload = {
        userAccess: {
          username: formData.username,
          password: formData.password,
          accessLevel: formData.accessLevel
        }
      };

      // Call the API to activate login
      const response = await axios.post(
        API_ENDPOINTS.EMPLOYEES.ACTIVATE_LOGIN(employeeId),
        payload,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Show success message
      setSnackbar({
        open: true,
        message: 'Login access activated successfully',
        severity: 'success'
      });

      // Navigate back to employee list with notification
      setTimeout(() => {
        navigate(ROUTES.CUSTOMER.EMPLOYEES.LIST, {
          state: {
            notification: {
              type: 'success',
              message: `Login access activated for ${employee.fullName || employee.firstName + ' ' + employee.lastName}`
            }
          }
        });
      }, 2000);
    } catch (error) {
      console.error('Error activating login:', error);
      let errorMessage = 'Failed to activate login';
      
      // Extract error message from response if available
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle going back
  const handleBack = () => {
    navigate(ROUTES.CUSTOMER.EMPLOYEES.LIST);
  };

  if (loading && !employee) {
    return (
      <Box p={3} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !employee) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
        <Button 
          variant="outlined" 
          onClick={handleBack} 
          startIcon={<ArrowBack />}
          sx={{ mt: 2 }}
        >
          Back to Employee List
        </Button>
      </Box>
    );
  }

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Button 
            variant="outlined" 
            startIcon={<ArrowBack />} 
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h5">Activate Login Access</Typography>
        </Box>

        {employee && (
          <Card sx={{ p: 3, mb: 3, bgcolor: 'background.default' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1">Employee:</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {employee.fullName || `${employee.firstName || ''} ${employee.lastName || ''}`.trim()}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1">Employee ID:</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {employee.employeeId || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1">Email:</Typography>
                <Typography variant="body1">
                  {employee.email || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1">Department:</Typography>
                <Typography variant="body1">
                  {employee.department?.departmentName || '-'}
                </Typography>
              </Grid>
            </Grid>
          </Card>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6">Login Credentials</Typography>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                error={!!errors.username}
                helperText={errors.username}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="password"
                label="Password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="password"
                label="Confirm Password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.accessLevel}>
                <InputLabel id="access-level-label">Access Level</InputLabel>
                <Select
                  labelId="access-level-label"
                  name="accessLevel"
                  value={formData.accessLevel}
                  onChange={handleChange}
                  label="Access Level"
                  required
                >
                  <MenuItem value="CUSTOMER_EMPLOYEE">Employee</MenuItem>
                  <MenuItem value="CUSTOMER_MANAGER">Manager</MenuItem>
                  <MenuItem value="CUSTOMER_ADMIN">Admin</MenuItem>
                </Select>
                {errors.accessLevel && (
                  <FormHelperText>{errors.accessLevel}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleBack}
                  sx={{ mr: 2 }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                >
                  {loading ? 'Activating...' : 'Activate Login'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default EmployeeLoginActivation;
